/**
 * Survey Page Component for EEG Survey Integration.
 * Main UI where users take the survey.
 * Kept separate from existing codebase.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Check, Loader, Brain, AlertCircle, Wifi, WifiOff, Usb, ArrowLeft } from 'lucide-react';
import toast from 'react-hot-toast';
import { surveyService } from '../../services/surveyService';
import serialService from '../../services/eeg/serialService';
import './SurveyPage.css';

const SurveyPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [surveys, setSurveys] = useState([]);
  const [survey, setSurvey] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [responseId, setResponseId] = useState(null);
  const [questionDisplayedAt, setQuestionDisplayedAt] = useState({});
  const [saveStatus, setSaveStatus] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [surveyStarted, setSurveyStarted] = useState(false);
  const [surveyCompleted, setSurveyCompleted] = useState(false);
  const [showSurveySelection, setShowSurveySelection] = useState(false);
  
  // EEG Serial connection state
  const [isEEGConnected, setIsEEGConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [eegReadings, setEegReadings] = useState([]);
  const [currentQuestionId, setCurrentQuestionId] = useState(null);
  const [postAnswerTimer, setPostAnswerTimer] = useState(null);
  const batchSubmissionInterval = useRef(null);
  // Use refs to always access current state values in callbacks
  const currentQuestionIdRef = useRef(null);
  const questionDisplayedAtRef = useRef({});
  const saveStatusRef = useRef({});
  const answersRef = useRef({});
  // Track question windows: { questionId: { startTime, endTime, answerSubmittedAt } }
  // Windows are from "Next" button to "Next" button (or survey start/end for first/last question)
  const questionWindowsRef = useRef({});
  const lastBatchSubmissionTime = useRef(Date.now());

  // Sync refs with state whenever they change
  useEffect(() => {
    currentQuestionIdRef.current = currentQuestionId;
  }, [currentQuestionId]);
  
  useEffect(() => {
    questionDisplayedAtRef.current = questionDisplayedAt;
  }, [questionDisplayedAt]);
  
  useEffect(() => {
    saveStatusRef.current = saveStatus;
  }, [saveStatus]);
  
  useEffect(() => {
    answersRef.current = answers;
  }, [answers]);

  // Load survey and questions
  useEffect(() => {
    loadSurvey();
    
    // Set up serial service callbacks (only show alerts when on survey page)
    const isOnSurveyPage = () => {
      return location.pathname === '/survey' || location.pathname.startsWith('/survey/');
    };
    
    serialService.setOnConnectionChange((connected) => {
      setIsEEGConnected(connected);
      // Only show disconnect alert if we're on the survey page
      if (!connected && isOnSurveyPage()) {
        toast.error('EEG device disconnected');
      }
    });
    
    serialService.setOnReading((reading) => {
      handleEEGReading(reading);
    });
    
    // Check browser compatibility
    if (!serialService.isAvailable()) {
      console.warn('Web Serial API not available - EEG connection will not work');
    }
    
    // Cleanup on unmount or when navigating away
    return () => {
      if (batchSubmissionInterval.current) {
        clearInterval(batchSubmissionInterval.current);
      }
      if (postAnswerTimer) {
        clearTimeout(postAnswerTimer);
      }
      // Clear callbacks to prevent alerts on other pages
      serialService.setOnConnectionChange(null);
      serialService.setOnReading(null);
      // Only disconnect if we're actually leaving survey pages
      // (Don't disconnect if user is navigating to survey results)
      if (!location.pathname.startsWith('/survey')) {
        serialService.disconnect().catch(console.error);
      }
    };
  }, [location.pathname]);

  const loadSurvey = async () => {
    try {
      setLoading(true);
      // Get all available surveys
      const surveysList = await surveyService.getSurveys();
      console.log('[SurveyPage] Loaded surveys:', surveysList);
      
      if (surveysList && surveysList.length > 0) {
        setSurveys(surveysList);
        // If only one survey, load it automatically
        if (surveysList.length === 1) {
          const surveyDetails = await surveyService.getSurvey(surveysList[0].id);
          console.log('[SurveyPage] Loaded survey details:', surveyDetails);
          setSurvey(surveyDetails);
          setQuestions(surveyDetails.questions || []);
        } else {
          // Multiple surveys - show selection screen
          setShowSurveySelection(true);
        }
      } else {
        console.warn('[SurveyPage] No surveys found in response');
        toast.error('No surveys available');
      }
    } catch (error) {
      console.error('[SurveyPage] Error loading survey:', error);
      console.error('[SurveyPage] Error response:', error.response?.data);
      console.error('[SurveyPage] Error message:', error.message);
      toast.error(`Failed to load survey: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const selectSurvey = async (surveyId) => {
    try {
      setLoading(true);
      const surveyDetails = await surveyService.getSurvey(surveyId);
      setSurvey(surveyDetails);
      setQuestions(surveyDetails.questions || []);
      setShowSurveySelection(false);
    } catch (error) {
      console.error('Error loading survey:', error);
      toast.error('Failed to load survey');
    } finally {
      setLoading(false);
    }
  };

  // Handle EEG device connection
  const handleConnectDevice = async () => {
    console.log('[SurveyPage] handleConnectDevice called');
    if (!serialService.isAvailable()) {
      console.error('[SurveyPage] Web Serial API not available');
      toast.error('Web Serial API is not available. Please use Chrome or Edge browser.');
      return;
    }

    try {
      setIsConnecting(true);
      console.log('[SurveyPage] Connecting to serial device...');
      await serialService.connect();
      console.log('[SurveyPage] Starting to read from device...');
      await serialService.startReading();
      console.log('[SurveyPage] Device connected and reading started');
      // Only show success toast if we're on the survey page
      if (location.pathname === '/survey' || location.pathname.startsWith('/survey/')) {
        toast.success('EEG device connected!');
      }
      
      // Start batch submission interval (every 5-10 seconds)
      console.log('[SurveyPage] Starting batch submission interval');
      startBatchSubmission();
    } catch (error) {
      console.error('[SurveyPage] Error connecting device:', error);
      toast.error(error.message || 'Failed to connect device');
    } finally {
      setIsConnecting(false);
    }
  };

  const handleDisconnectDevice = async () => {
    try {
      await serialService.disconnect();
      if (batchSubmissionInterval.current) {
        clearInterval(batchSubmissionInterval.current);
      }
      // Only show disconnect toast if we're on the survey page
      if (location.pathname === '/survey' || location.pathname.startsWith('/survey/')) {
        // Only show disconnect toast if we're on the survey page
      if (location.pathname === '/survey' || location.pathname.startsWith('/survey/')) {
        toast.success('Device disconnected');
      }
      }
    } catch (error) {
      console.error('Error disconnecting device:', error);
      toast.error('Failed to disconnect device');
    }
  };

  // Handle new EEG reading with question correlation
  const handleEEGReading = (reading) => {
    const questionWindows = questionWindowsRef.current;
    const readingTime = new Date(reading.timestamp || new Date());
    
    // Find which question window this reading falls into
    let phase = 'continuous';
    let questionId = null;
    
    // Check windows in reverse order (most recent first) to handle overlapping windows correctly
    const sortedWindows = Object.entries(questionWindows).sort((a, b) => {
      const timeA = new Date(a[1].startTime);
      const timeB = new Date(b[1].startTime);
      return timeB - timeA; // Most recent first
    });
    
    for (const [qId, window] of sortedWindows) {
      const startTime = new Date(window.startTime);
      const endTime = window.endTime ? new Date(window.endTime) : null;
      
      // Check if reading is within this question's window
      // For closed windows, readings must be <= endTime
      // For open windows (endTime is null), readings must be >= startTime
      if (readingTime >= startTime && (!endTime || readingTime <= endTime)) {
        questionId = qId;
        
        // Determine phase based on answer submission time
        if (window.answerSubmittedAt) {
          const answerTime = new Date(window.answerSubmittedAt);
          if (readingTime < answerTime) {
            phase = 'before_answer';
          } else {
            // Reading is after answer submission - mark as after_answer
            phase = 'after_answer';
          }
        } else {
          // No answer submitted yet - it's before answer
          phase = 'before_answer';
        }
        break;
      }
    }

    // Add to readings with correlation
    const correlatedReading = {
      ...reading,
      question_id: questionId,
      phase: phase,
      timestamp: reading.timestamp || readingTime.toISOString(),
    };

    console.log('[SurveyPage] Correlated reading:', {
      timestamp: correlatedReading.timestamp,
      phase: correlatedReading.phase,
      question_id: correlatedReading.question_id,
      beta: correlatedReading.beta?.toFixed(2),
      alpha: correlatedReading.alpha?.toFixed(2)
    });

    setEegReadings(prev => {
      const newReadings = [...prev, correlatedReading];
      return newReadings;
    });
  };

  // Start batch submission of EEG readings
  const startBatchSubmission = () => {
    // Submit batches every 5-10 seconds or when buffer reaches 20 readings
    batchSubmissionInterval.current = setInterval(async () => {
      await submitEEGReadingsBatch();
    }, 5000); // Every 5 seconds
  };

  // Submit EEG readings batch to backend
  const submitEEGReadingsBatch = async (overrideResponseId = null) => {
    const responseIdToUse = overrideResponseId || responseId;
    if (!responseIdToUse) {
      console.log('[SurveyPage] submitEEGReadingsBatch: No responseId, skipping');
      return;
    }

    // Use correlated readings from state (these have question_id and phase already set)
    // Get new readings from serial service and correlate them first
    const rawReadings = serialService.getReadings();
    
    if (rawReadings.length === 0 && eegReadings.length === 0) {
      console.log('[SurveyPage] submitEEGReadingsBatch: No readings to submit, skipping');
      return;
    }

    try {
      console.log('[SurveyPage] Submitting EEG readings batch to backend...');
      
      // Correlate new raw readings with question windows
      const questionWindows = questionWindowsRef.current;
      const now = new Date();
      
      const correlatedNewReadings = rawReadings.map(reading => {
        let phase = 'continuous';
        let questionId = null;
        const readingTime = new Date(reading.timestamp || now);

        // Find which question window this reading falls into
        // Check windows in reverse order (most recent first) to handle overlapping windows correctly
        const sortedWindows = Object.entries(questionWindows).sort((a, b) => {
          const timeA = new Date(a[1].startTime);
          const timeB = new Date(b[1].startTime);
          return timeB - timeA; // Most recent first
        });
        
        for (const [qId, window] of sortedWindows) {
          const startTime = new Date(window.startTime);
          const endTime = window.endTime ? new Date(window.endTime) : null;
          
          // Check if reading is within this question's window
          // For closed windows, readings must be <= endTime
          // For open windows (endTime is null), readings must be >= startTime
          if (readingTime >= startTime && (!endTime || readingTime <= endTime)) {
            questionId = qId;
            
            // Determine phase based on answer submission time
            if (window.answerSubmittedAt) {
              const answerTime = new Date(window.answerSubmittedAt);
              if (readingTime < answerTime) {
                phase = 'before_answer';
              } else {
                // Reading is after answer submission - mark as after_answer
                phase = 'after_answer';
              }
            } else {
              // No answer submitted yet - it's before answer
              phase = 'before_answer';
            }
            break;
          }
        }

        return {
          response: responseIdToUse,
          question: questionId,
          timestamp: reading.timestamp || now.toISOString(),
          phase: phase,
          delta: reading.delta,
          theta: reading.theta,
          alpha: reading.alpha,
          beta: reading.beta,
          gamma: reading.gamma,
          raw_serial_data: reading.raw_serial_data,
        };
      });

      // Combine with already correlated readings from state
      const stateReadings = eegReadings.map(reading => ({
        response: responseIdToUse,
        question: reading.question_id || null,
        timestamp: reading.timestamp || new Date().toISOString(),
        phase: reading.phase || 'continuous',
        delta: reading.delta,
        theta: reading.theta,
        alpha: reading.alpha,
        beta: reading.beta,
        gamma: reading.gamma,
        raw_serial_data: reading.raw_serial_data,
      }));

      // Combine all readings
      const formattedReadings = [...stateReadings, ...correlatedNewReadings];
      
      if (formattedReadings.length === 0) {
        return;
      }

      const result = await surveyService.submitEEGReadings(formattedReadings);
      
      console.log('[SurveyPage] EEG readings submitted successfully:', {
        count: formattedReadings.length,
        result: result,
        withQuestionCorrelation: formattedReadings.filter(r => r.question !== null).length
      });
      
      // Clear submitted readings from buffer and state
      serialService.clearReadings();
      setEegReadings([]);
      
      console.log(`[SurveyPage] Successfully submitted ${formattedReadings.length} EEG readings (${formattedReadings.filter(r => r.question !== null).length} with question correlation) and cleared buffer`);
    } catch (error) {
      console.error('[SurveyPage] Error submitting EEG readings:', {
        error: error,
        errorMessage: error.message,
        readingsCount: formattedReadings.length,
        responseId: responseId
      });
      // Don't clear buffer on error - will retry
    }
  };

  const startSurvey = async () => {
    try {
      setIsSubmitting(true);
      
      // Clear all accumulated EEG readings before starting survey
      if (isEEGConnected) {
        serialService.clearReadings();
        setEegReadings([]);
        console.log('[SurveyPage] Cleared all accumulated EEG readings before starting survey');
      }
      
      const response = await surveyService.startSurvey(survey.id);
      setResponseId(response.id);
      setSurveyStarted(true);
      
      // Create EEG session if device is connected
      if (isEEGConnected) {
        try {
          await surveyService.createEEGSession(response.id);
          console.log('[SurveyPage] EEG session created');
        } catch (error) {
          console.error('[SurveyPage] Error creating EEG session:', error);
          // Don't fail survey start if session creation fails
        }
      }
      
      // Mark first question as displayed and set as current
      // For first question, window starts when survey starts (no "Next" button before it)
      if (questions.length > 0) {
        const firstQuestion = questions[0];
        const displayTime = new Date().toISOString();
        const questionDisplayTimes = { [firstQuestion.id]: displayTime };
        setQuestionDisplayedAt(questionDisplayTimes);
        questionDisplayedAtRef.current = questionDisplayTimes;
        setCurrentQuestionId(firstQuestion.id);
        currentQuestionIdRef.current = firstQuestion.id;
        
        // Initialize first question window (starts now, ends when "Next" is clicked)
        questionWindowsRef.current[firstQuestion.id] = {
          startTime: displayTime,
          endTime: null, // Will be set when "Next" is clicked
          answerSubmittedAt: null
        };
      }
      
      // Start batch submission if EEG is connected
      if (isEEGConnected) {
        startBatchSubmission();
      }
      
      toast.success('Survey started!');
    } catch (error) {
      console.error('Error starting survey:', error);
      toast.error('Failed to start survey');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAnswerChange = (questionId, answerText) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answerText
    }));
  };

  const submitAnswer = async (questionId) => {
    if (!responseId) {
      toast.error('Survey session not started');
      throw new Error('Survey session not started');
    }

    const answerValue = answers[questionId];
    if (answerValue === undefined || answerValue === null || answerValue === '') {
      toast.error('Please select an answer');
      throw new Error('Answer is required');
    }
    
    // Convert to string for storage
    const answerText = answerValue.toString();

    try {
      setIsSubmitting(true);
      const questionDisplayedTime = questionDisplayedAt[questionId] || new Date().toISOString();
      const answerSubmittedTime = new Date().toISOString();

      await surveyService.submitAnswer(responseId, {
        questionId: questionId,
        answerText: answerText.trim(),
        questionDisplayedAt: questionDisplayedTime,
        answerSubmittedAt: answerSubmittedTime,
      });

      // Mark as saved (or updated if it was already saved)
      setSaveStatus(prev => {
        const updated = { ...prev, [questionId]: 'saved' };
        saveStatusRef.current = updated;
        return updated;
      });

      // Mark answer submission time in question window
      setAnswers(prev => {
        const updated = { ...prev, [questionId + '_submitted']: answerSubmittedTime };
        answersRef.current = updated;
        return updated;
      });
      
      // Update question window with answer submission time
      if (questionWindowsRef.current[questionId]) {
        questionWindowsRef.current[questionId].answerSubmittedAt = answerSubmittedTime;
      }

      toast.success('Answer saved!', { duration: 2000 });
    } catch (error) {
      console.error('Error submitting answer:', error);
      toast.error('Failed to save answer');
      setSaveStatus(prev => {
        const updated = { ...prev, [questionId]: 'error' };
        saveStatusRef.current = updated;
        return updated;
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNext = async () => {
    const currentQuestion = questions[currentQuestionIndex];
    
    // REQUIRE answer before proceeding - don't allow moving forward without an answer
    if (!currentQuestion) {
      toast.error('No question available');
      return;
    }
    
    const currentAnswer = answers[currentQuestion.id];
    if (!currentAnswer || currentAnswer === '' || currentAnswer === null || currentAnswer === undefined) {
      toast.error('Please select an answer before proceeding');
      return;
    }
    
    // Submit answer - this must succeed before proceeding
    try {
      await submitAnswer(currentQuestion.id);
    } catch (error) {
      console.error('Error submitting answer in handleNext:', error);
      // Don't proceed if answer submission fails
      toast.error('Failed to save answer. Please try again.');
      return;
    }
    
    // Only after successful answer submission, proceed to next question
    const nextButtonTime = new Date().toISOString();
    
    // Close current question window (mark end time when "Next" is clicked)
    if (questionWindowsRef.current[currentQuestion.id]) {
      questionWindowsRef.current[currentQuestion.id].endTime = nextButtonTime;
    }

    if (currentQuestionIndex < questions.length - 1) {
      // Move to next question
      const nextIndex = currentQuestionIndex + 1;
      setCurrentQuestionIndex(nextIndex);
      
      // Mark next question as displayed and set as current
      const nextQuestion = questions[nextIndex];
      if (nextQuestion) {
        const displayTime = new Date().toISOString();
        setQuestionDisplayedAt(prev => {
          const updated = { ...prev, [nextQuestion.id]: displayTime };
          questionDisplayedAtRef.current = updated;
          return updated;
        });
        setCurrentQuestionId(nextQuestion.id);
        currentQuestionIdRef.current = nextQuestion.id;
        
        // Start new question window (starts when "Next" is clicked)
        questionWindowsRef.current[nextQuestion.id] = {
          startTime: displayTime,
          endTime: null, // Will be set when next "Next" is clicked (or survey completes)
          answerSubmittedAt: null
        };
      }
    } else {
      // Last question - complete survey (answer already saved above)
      // Last question window ends when survey completes
      await completeSurvey();
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const completeSurvey = async () => {
    const savedResponseId = responseId; // Save responseId before state changes
    const completionTime = new Date().toISOString();
    
    try {
      setIsSubmitting(true);
      
      // Ensure last answer is submitted before completing survey
      const currentQuestion = questions[currentQuestionIndex];
      if (!currentQuestion) {
        toast.error('No question available');
        return;
      }
      
      const currentAnswer = answers[currentQuestion.id];
      if (!currentAnswer || currentAnswer === '' || currentAnswer === null || currentAnswer === undefined) {
        toast.error('Please answer the last question before completing the survey');
        return;
      }
      
      // Submit last answer if not already saved
      if (!saveStatus[currentQuestion.id]) {
        try {
          await submitAnswer(currentQuestion.id);
        } catch (error) {
          console.error('[SurveyPage] Error submitting last answer:', error);
          toast.error('Failed to save answer. Please try again.');
          return;
        }
      }
      
      // Close last question window (ends when survey completes - no "Next" button after last question)
      if (currentQuestion && questionWindowsRef.current[currentQuestion.id]) {
        questionWindowsRef.current[currentQuestion.id].endTime = completionTime;
      }

      // Submit any remaining EEG readings (use saved responseId)
      if (savedResponseId) {
        await submitEEGReadingsBatch(savedResponseId);
      }

      // Stop EEG reading and batch submission
      if (isEEGConnected) {
        await serialService.stopReading();
        if (batchSubmissionInterval.current) {
          clearInterval(batchSubmissionInterval.current);
          batchSubmissionInterval.current = null;
        }
      }

      // Mark response as completed
      if (savedResponseId) {
        await surveyService.updateResponse(savedResponseId, 'completed');
      }
      
      setSurveyCompleted(true);
      toast.success('Survey completed!');
    } catch (error) {
      console.error('Error completing survey:', error);
      toast.error('Failed to complete survey');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="survey-page">
        <div className="survey-loading">
          <Loader className="spinner" />
          <p>Loading survey...</p>
        </div>
      </div>
    );
  }

  if (showSurveySelection) {
    return (
      <div className="survey-page">
        <div className="survey-start">
          <div className="survey-start-content">
            <Brain className="survey-icon" />
            <h1>Select a Survey</h1>
            <p className="survey-description">Choose which survey you would like to take</p>
            
            <div className="survey-selection-list">
              {surveys.map((s) => (
                <button
                  key={s.id}
                  className="survey-option-card"
                  onClick={() => selectSurvey(s.id)}
                  disabled={loading}
                >
                  <div>
                    <h3>{s.title}</h3>
                    {s.description && <p>{s.description}</p>}
                  </div>
                  <span className="survey-option-arrow">→</span>
                </button>
              ))}
            </div>
            
            <div className="survey-start-actions">
              <button
                className="btn-secondary btn-view-past-surveys"
                onClick={() => navigate('/survey/history')}
              >
                View Past Surveys
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!survey && !loading) {
    return (
      <div className="survey-page">
        <div className="survey-error">
          <AlertCircle />
          <p>No survey available</p>
          <p style={{ fontSize: '0.9rem', marginTop: '1rem', opacity: 0.8 }}>
            Please check if surveys are loaded in the database.
          </p>
        </div>
      </div>
    );
  }

  if (!surveyStarted) {
    return (
      <div className="survey-page">
        <div className="survey-start">
          <div className="survey-start-content">
            <Brain className="survey-icon" />
            <h1>{survey.title}</h1>
            <p className="survey-description">{survey.description || 'Resilience prediction survey with EEG biofeedback'}</p>
            <p className="survey-info">This survey contains {questions.length} questions</p>
            
            {/* EEG Device Connection */}
            <div className="device-connection-panel">
              <h3>EEG Device Connection</h3>
              <div className="device-status">
                {isEEGConnected ? (
                  <>
                    <Wifi className="status-icon connected" />
                    <span className="status-text">Connected</span>
                    <button
                      className="btn-disconnect"
                      onClick={handleDisconnectDevice}
                      disabled={isConnecting}
                    >
                      Disconnect
                    </button>
                  </>
                ) : (
                  <>
                    <WifiOff className="status-icon disconnected" />
                    <span className="status-text">Not Connected</span>
                    <button
                      className="btn-connect"
                      onClick={handleConnectDevice}
                      disabled={isConnecting || !serialService.isAvailable()}
                    >
                      {isConnecting ? <Loader className="spinner-small" /> : <Usb size={16} />}
                      {isConnecting ? 'Connecting...' : 'Connect Device'}
                    </button>
                  </>
                )}
              </div>
              {!serialService.isAvailable() && (
                <p className="browser-warning">
                  ⚠️ Web Serial API requires Chrome or Edge browser
                </p>
              )}
              <p className="device-help">
                Connect your Arduino device before starting the survey for EEG data collection.
                You can also connect during the survey.
              </p>
            </div>
            
            <div className="survey-start-actions">
              <button
                className="btn-start-survey"
                onClick={startSurvey}
                disabled={isSubmitting}
              >
                {isSubmitting ? <Loader className="spinner-small" /> : 'Start Survey'}
              </button>
              <button
                className="btn-secondary btn-view-past-surveys"
                onClick={() => navigate('/survey/history')}
              >
                View Past Surveys
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (surveyCompleted) {
    return (
      <div className="survey-page">
        <div className="survey-complete">
          <div className="survey-complete-content">
            <Check className="check-icon" />
            <h1>Survey Completed!</h1>
            <p>Thank you for completing the survey.</p>
            <div className="survey-complete-actions">
              <button
                className="btn-primary"
                onClick={() => navigate(`/survey/results/${responseId}`)}
              >
                View Results
              </button>
              <button
                className="btn-secondary"
                onClick={() => navigate('/survey/history')}
              >
                View History
              </button>
              <button
                className="btn-secondary"
                onClick={() => navigate('/survey')}
              >
                Start New Survey
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentQuestionIndex];
  const progress = ((currentQuestionIndex + 1) / questions.length) * 100;
  const isAnswerSaved = saveStatus[currentQuestion.id] === 'saved';

  return (
    <div className="survey-page">
      <div className="survey-container">
        {/* Back to Surveys List Button */}
        <button
          className="btn-back-to-list-survey"
          onClick={() => navigate('/survey/history')}
          title="Back to Surveys List"
        >
          <ArrowLeft size={18} />
          Back to Surveys List
        </button>
        
        {/* Progress Bar */}
        <div className="survey-progress">
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${progress}%` }}></div>
          </div>
          <div className="progress-text">
            Question {currentQuestionIndex + 1} of {questions.length}
          </div>
          
          {/* EEG Connection Status */}
          <div className="eeg-status-indicator">
            {isEEGConnected ? (
              <span className="eeg-status connected">
                <Wifi size={14} /> EEG Connected ({eegReadings.length} readings)
              </span>
            ) : (
              <span className="eeg-status disconnected">
                <WifiOff size={14} /> EEG Not Connected
                <button
                  className="btn-connect-inline"
                  onClick={handleConnectDevice}
                  disabled={isConnecting || !serialService.isAvailable()}
                >
                  Connect
                </button>
              </span>
            )}
          </div>
        </div>

        {/* Question Card */}
        <div className="question-card">
          <div className="question-header">
            <span className="question-number">Q{currentQuestionIndex + 1}</span>
            {isAnswerSaved && (
              <span className="save-status saved">
                <Check size={16} /> Saved
              </span>
            )}
          </div>
          
          <h2 className="question-text">{currentQuestion.question_text}</h2>
          
          {currentQuestion.help_text && (
            <p className="question-help">{currentQuestion.help_text}</p>
          )}

          {/* Multiple Choice Options (for RSJT survey) */}
          {currentQuestion.answer_options && currentQuestion.answer_options.length > 0 ? (
            <div className="multiple-choice-container">
              <div className="options-list">
                {currentQuestion.answer_options.map((option, index) => (
                  <label key={index} className={`option-item ${answers[currentQuestion.id] === option ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name={`question-${currentQuestion.id}`}
                      value={option}
                      checked={answers[currentQuestion.id] === option}
                      onChange={(e) => {
                        handleAnswerChange(currentQuestion.id, e.target.value);
                      }}
                      className="option-radio"
                    />
                    <span className="option-text">{option}</span>
                  </label>
                ))}
              </div>
            </div>
          ) : (
            /* Rating Bar (for Prediction Survey) */
            <div className="rating-container">
              <div className="rating-bar-wrapper">
                <input
                  type="range"
                  min="0"
                  max="20"
                  step="1"
                  value={answers[currentQuestion.id] ? (parseFloat(answers[currentQuestion.id]) * 2) : 0}
                  onChange={(e) => {
                    const value = parseFloat(e.target.value) / 2; // Convert to 0-10 with 0.5 steps
                    handleAnswerChange(currentQuestion.id, value.toString());
                  }}
                  className="rating-slider"
                />
                <div className="rating-labels">
                  <span className="rating-label">0</span>
                  <span className="rating-label">5</span>
                  <span className="rating-label">10</span>
                </div>
              </div>
              <div className="rating-value-display">
                <span className="rating-value">{answers[currentQuestion.id] || '0.0'}</span>
                <span className="rating-unit">/ 10</span>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="question-navigation">
            <button
              className="btn-nav btn-prev"
              onClick={handlePrevious}
              disabled={currentQuestionIndex === 0 || isSubmitting}
            >
              <ChevronLeft size={20} />
              Previous
            </button>
            
            <button
              className="btn-nav btn-next"
              onClick={handleNext}
              disabled={isSubmitting || !answers[currentQuestion.id] || answers[currentQuestion.id] === ''}
              title={!answers[currentQuestion.id] || answers[currentQuestion.id] === '' ? 'Please select an answer to continue' : ''}
            >
              {currentQuestionIndex === questions.length - 1 ? 'Complete' : 'Next'}
              <ChevronRight size={20} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SurveyPage;
